import React, { useRef, useEffect, useState } from 'react';

/**
 * Interactive Chart Component with hover tooltips and zoom
 */
function InteractiveChart({ data, type = 'line', title, onHover }) {
  const canvasRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [zoom, setZoom] = useState({ start: 0, end: 1 });

  useEffect(() => {
    if (!canvasRef.current || !data || data.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 2;

    canvas.width = canvas.clientWidth * dpr;
    canvas.height = 400 * dpr;
    ctx.scale(dpr, dpr);

    const width = canvas.clientWidth;
    const height = 400;
    const padding = { top: 50, right: 60, bottom: 70, left: 80 };

    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Clear canvas
    ctx.fillStyle = '#0a0a0f';
    ctx.fillRect(0, 0, width, height);

    // Apply zoom
    const startIdx = Math.floor(data.length * zoom.start);
    const endIdx = Math.ceil(data.length * zoom.end);
    const visibleData = data.slice(startIdx, endIdx);

    if (visibleData.length === 0) return;

    // Extract values based on type
    const values = visibleData.map(d => d.value || d.price || d.pnl || 0);
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const range = maxValue - minValue;

    // Scales
    const xScale = (index) => padding.left + (index / (visibleData.length - 1)) * chartWidth;
    const yScale = (value) => padding.top + chartHeight - ((value - minValue) / range) * chartHeight;

    // Grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.1)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + chartWidth, y);
      ctx.stroke();
    }

    // Draw chart based on type
    if (type === 'line') {
      drawLineChart(ctx, visibleData, values, xScale, yScale, chartWidth, chartHeight, padding);
    } else if (type === 'area') {
      drawAreaChart(ctx, visibleData, values, xScale, yScale, chartWidth, chartHeight, padding);
    } else if (type === 'candle') {
      drawCandlestickChart(ctx, visibleData, xScale, yScale, chartWidth);
    }

    // Axes
    drawAxes(ctx, visibleData, values, minValue, maxValue, width, height, padding, chartWidth, chartHeight);

    // Title
    ctx.fillStyle = '#ecf0f1';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(title || 'Chart', width / 2, 30);

  }, [data, type, title, zoom]);

  const handleMouseMove = (e) => {
    if (!canvasRef.current || !data) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const padding = { left: 80, right: 60, top: 50, bottom: 70 };
    const chartWidth = rect.width - padding.left - padding.right;

    if (x >= padding.left && x <= rect.width - padding.right) {
      const startIdx = Math.floor(data.length * zoom.start);
      const endIdx = Math.ceil(data.length * zoom.end);
      const visibleData = data.slice(startIdx, endIdx);
      
      const index = Math.floor(((x - padding.left) / chartWidth) * visibleData.length);
      const point = visibleData[index];

      if (point) {
        setTooltip({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          data: point,
        });

        if (onHover) onHover(point);
      }
    } else {
      setTooltip(null);
    }
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.1 : -0.1;
    const newRange = Math.min(1, Math.max(0.1, (zoom.end - zoom.start) + delta));
    
    setZoom(prev => ({
      start: Math.max(0, prev.start),
      end: Math.min(1, prev.start + newRange),
    }));
  };

  return (
    <div className="interactive-chart-container">
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '400px', cursor: 'crosshair' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
      />
      
      {tooltip && (
        <div
          className="chart-tooltip"
          style={{
            position: 'absolute',
            left: tooltip.x + 15,
            top: tooltip.y - 50,
            background: 'rgba(0,0,0,0.9)',
            color: '#ecf0f1',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '0.9rem',
            pointerEvents: 'none',
            border: '1px solid rgba(102,126,234,0.5)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            zIndex: 1000,
          }}
        >
          <div><strong>{tooltip.data.date || tooltip.data.label}</strong></div>
          <div style={{ marginTop: '5px' }}>
            Value: <strong>{(tooltip.data.value || tooltip.data.price || tooltip.data.pnl || 0).toFixed(2)}</strong>
          </div>
          {tooltip.data.change && (
            <div style={{ color: tooltip.data.change >= 0 ? '#2ecc71' : '#e74c3c' }}>
              Change: {tooltip.data.change >= 0 ? '+' : ''}{tooltip.data.change.toFixed(2)}%
            </div>
          )}
        </div>
      )}

      <div className="chart-controls">
        <button onClick={() => setZoom({ start: 0, end: 1 })} className="btn-chart-zoom">
          Reset Zoom
        </button>
        <span className="zoom-info">Scroll to zoom • Drag to pan</span>
      </div>
    </div>
  );
}

// Helper drawing functions
function drawLineChart(ctx, data, values, xScale, yScale, chartWidth, chartHeight, padding) {
  ctx.strokeStyle = '#667eea';
  ctx.lineWidth = 3;
  ctx.shadowBlur = 10;
  ctx.shadowColor = 'rgba(102,126,234,0.5)';
  
  ctx.beginPath();
  values.forEach((val, i) => {
    const x = xScale(i);
    const y = yScale(val);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
  ctx.shadowBlur = 0;

  // Draw points
  ctx.fillStyle = '#667eea';
  values.forEach((val, i) => {
    const x = xScale(i);
    const y = yScale(val);
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, Math.PI * 2);
    ctx.fill();
  });
}

function drawAreaChart(ctx, data, values, xScale, yScale, chartWidth, chartHeight, padding) {
  const gradient = ctx.createLinearGradient(0, padding.top, 0, padding.top + chartHeight);
  gradient.addColorStop(0, 'rgba(102,126,234,0.4)');
  gradient.addColorStop(1, 'rgba(102,126,234,0.05)');

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.moveTo(xScale(0), yScale(0));
  values.forEach((val, i) => {
    ctx.lineTo(xScale(i), yScale(val));
  });
  ctx.lineTo(xScale(values.length - 1), padding.top + chartHeight);
  ctx.lineTo(xScale(0), padding.top + chartHeight);
  ctx.closePath();
  ctx.fill();

  // Line on top
  ctx.strokeStyle = '#667eea';
  ctx.lineWidth = 2;
  ctx.beginPath();
  values.forEach((val, i) => {
    const x = xScale(i);
    const y = yScale(val);
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.stroke();
}

function drawCandlestickChart(ctx, data, xScale, yScale, chartWidth) {
  const barWidth = (chartWidth / data.length) * 0.6;

  data.forEach((d, i) => {
    const x = xScale(i);
    const open = yScale(d.open || d.price);
    const close = yScale(d.close || d.price);
    const high = yScale(d.high || d.price * 1.01);
    const low = yScale(d.low || d.price * 0.99);

    const isGreen = close < open;
    ctx.fillStyle = isGreen ? '#2ecc71' : '#e74c3c';
    ctx.strokeStyle = isGreen ? '#2ecc71' : '#e74c3c';

    // Wick
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, high);
    ctx.lineTo(x, low);
    ctx.stroke();

    // Body
    ctx.fillRect(x - barWidth / 2, Math.min(open, close), barWidth, Math.abs(close - open));
  });
}

function drawAxes(ctx, data, values, minValue, maxValue, width, height, padding, chartWidth, chartHeight) {
  ctx.fillStyle = '#95a5a6';
  ctx.font = '12px Arial';

  // Y-axis labels
  ctx.textAlign = 'right';
  for (let i = 0; i <= 5; i++) {
    const value = minValue + ((maxValue - minValue) / 5) * i;
    const y = padding.top + chartHeight - (chartHeight / 5) * i;
    ctx.fillText(value.toFixed(0), padding.left - 10, y + 4);
  }

  // X-axis labels
  ctx.textAlign = 'center';
  const step = Math.ceil(data.length / 8);
  for (let i = 0; i < data.length; i += step) {
    const x = padding.left + (i / (data.length - 1)) * chartWidth;
    const label = data[i].date || data[i].label || i.toString();
    ctx.save();
    ctx.translate(x, height - padding.bottom + 20);
    ctx.rotate(-Math.PI / 4);
    ctx.fillText(label, 0, 0);
    ctx.restore();
  }
}

export default InteractiveChart;
